// src/pages/offlineSales/CustomerPaymentDetailPage.jsx
//
// Read-only view of one customer receipt: customer, payment details/proof, and how the amount
// was allocated (opening balance first, then invoices oldest-first). Reached from
// CustomerPaymentsPage.jsx. Backend: customerPaymentService.getCustomerPaymentById
// (rettzy-backend/src/modules/offlineSales/services/customerPayment.service.js).
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  HandCoins, ArrowLeft, User, Phone, FileText, XCircle, Ban, Landmark, Receipt, Paperclip, CalendarClock,
} from 'lucide-react';
import { PageHeader, DataTable } from '../../components/common';
import { getCustomerPayment, voidCustomerPayment } from '../../services/api/offlineSales/customerPayments';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { formatDate } from '../../utils/dateUtils';
import { getImageUrl } from '../../utils/imageUtils';
import statementStyles from './CustomerStatementPage.module.css';
import chequeStyles from './ChequesPage.module.css';
import styles from './CustomerPaymentDetailPage.module.css';

const CustomerPaymentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, formatCurrency } = useLocalization();
  const { success, error: showError, confirm } = useAlert();
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission('edit_offline_sales_order');

  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPayment = useCallback(async () => {
    try {
      setLoading(true);
      setPayment(await getCustomerPayment(id));
    } catch (err) {
      showError(err?.response?.data?.message || t('offlineSales.cpFailedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [id, showError, t]);

  useEffect(() => { loadPayment(); }, [loadPayment]);

  const handleVoid = async () => {
    const confirmed = await confirm(t('offlineSales.cpVoidConfirm', { number: payment.paymentNumber }), { type: 'danger' });
    if (!confirmed) return;
    try {
      await voidCustomerPayment(payment.id);
      success(t('offlineSales.cpVoided'));
      loadPayment();
    } catch (err) {
      showError(err?.response?.data?.message || t('offlineSales.cpFailedToVoid'));
    }
  };

  const isVoid = payment?.status === 'void';
  const allocations = payment ? [
    ...(payment.openingBalanceAllocations || []).map((a) => ({
      id: `ob-${a.id}`, type: 'opening_balance', date: a.openingBalance?.asOfDate, amount: a.amount,
    })),
    ...(payment.paymentRecords || []).map((r) => ({
      id: `pr-${r.id}`, type: 'invoice', orderId: r.orderId, reference: r.EcomOrder?.orderNumber,
      invoiceTotal: r.EcomOrder?.grandTotal, amount: r.paymentAmount, isVoid: r.status === 'void',
    })),
  ] : [];

  const columns = [
    {
      key: 'type',
      header: t('offlineSales.cpAllocatedTo'),
      render: (row) => (row.type === 'opening_balance' ? (
        <span className={statementStyles.typeCell}><Landmark size={14} />{t('offlineSales.openingBalance') || 'Opening Balance'}</span>
      ) : (
        <span className={statementStyles.typeCell}>
          <Receipt size={14} />
          <Link className={statementStyles.docLink} to={`/offline-sales/orders/${row.orderId}`}>{row.reference || `#${row.orderId}`}</Link>
        </span>
      )),
    },
    {
      key: 'date',
      header: t('offlineSales.cpAsOfDate'),
      render: (row) => (row.date ? formatDate(row.date) : <span className={statementStyles.mutedDash}>—</span>),
    },
    {
      key: 'invoiceTotal',
      header: t('offlineSales.cpInvoiceTotal'),
      align: 'right',
      render: (row) => (row.invoiceTotal != null
        ? <span className={statementStyles.amountCell}>{formatCurrency(row.invoiceTotal)}</span>
        : <span className={statementStyles.mutedDash}>—</span>),
    },
    {
      key: 'amount',
      header: t('offlineSales.cpAmountApplied'),
      align: 'right',
      render: (row) => (
        <span className={`${statementStyles.amountCell} ${statementStyles.creditAmount} ${row.isVoid ? styles.voidAmount : ''}`}>
          {formatCurrency(row.amount)}
        </span>
      ),
    },
  ];

  return (
    <div className={statementStyles.page}>
      <PageHeader
        title={payment?.paymentNumber || t('offlineSales.cpDetailTitle')}
        description={t('offlineSales.cpDetailTitle')}
        icon={HandCoins}
        actions={
          <div className={statementStyles.headerActions}>
            <button className={statementStyles.secondaryBtn} onClick={() => navigate('/offline-sales/customer-payments')}>
              <ArrowLeft size={16} /> {t('common.back')}
            </button>
            {payment && (
              <button className={statementStyles.secondaryBtn} onClick={() => navigate(`/offline-sales/customers/${payment.customerId}/statement`)}>
                <FileText size={15} /> {t('offlineSales.customerStatement')}
              </button>
            )}
            {payment && canWrite && !isVoid && (
              <button className={`${statementStyles.secondaryBtn} ${styles.dangerBtn}`} onClick={handleVoid}>
                <XCircle size={15} /> {t('offlineSales.cpVoid')}
              </button>
            )}
          </div>
        }
      />

      {payment && (
        <>
          {isVoid && (
            <div className={styles.voidBanner}>
              <Ban size={16} />
              <span>
                {t('offlineSales.cpStatus_void')}
                {payment.voidedAt && ` · ${formatDate(payment.voidedAt)}`}
                {payment.voidReason && ` · ${payment.voidReason}`}
              </span>
            </div>
          )}

          <div className={statementStyles.balanceCards}>
            <div className={statementStyles.balanceCard}>
              <div className={statementStyles.balanceCardLabel}><User size={14} />{t('offlineSales.customer')}</div>
              <div className={statementStyles.balanceCardValue}>{payment.customer?.name}</div>
              {payment.customer?.phone && <div className={styles.cardMeta}><Phone size={12} />{payment.customer.phone}</div>}
            </div>
            <div className={statementStyles.balanceCard}>
              <div className={statementStyles.balanceCardLabel}><HandCoins size={14} />{t('offlineSales.amount')}</div>
              <div className={statementStyles.balanceCardValue}>{formatCurrency(payment.amount)}</div>
              {parseFloat(payment.unallocatedAmount) > 0 && (
                <div className={styles.cardMeta}>{t('offlineSales.cpUnallocated')}: {formatCurrency(payment.unallocatedAmount)}</div>
              )}
              <span className={`${chequeStyles.statusBadge} ${isVoid ? chequeStyles.statusBounced : chequeStyles.statusCleared}`}>
                {t(`offlineSales.cpStatus_${payment.status}`)}
              </span>
            </div>
          </div>

          <div className={styles.detailCard}>
            <dl className={styles.detailGrid}>
              <div>
                <dt>{t('offlineSales.cpPaymentDate')}</dt>
                <dd>{formatDate(payment.paymentDate)}</dd>
              </div>
              <div>
                <dt>{t('offlineSales.cpMethod')}</dt>
                <dd>{payment.PaymentMethod?.label || '—'}</dd>
              </div>
              <div>
                <dt>{t('offlineSales.cpReference')}</dt>
                <dd>{payment.reference || '—'}</dd>
              </div>
              {payment.cheque && (
                <div>
                  <dt>{t('offlineSales.chqDate')}</dt>
                  <dd>
                    <CalendarClock size={12} /> {formatDate(payment.cheque.chequeDate)}
                    {' · '}{t(`offlineSales.chqStatus_${payment.cheque.status}`)}
                  </dd>
                </div>
              )}
              {payment.createdBy && (
                <div>
                  <dt>{t('offlineSales.cpReceivedBy')}</dt>
                  <dd>{payment.createdBy.name}</dd>
                </div>
              )}
              <div>
                <dt>{t('offlineSales.cpProof')}</dt>
                <dd>
                  {payment.proofUrl ? (
                    <a className={styles.proofLink} href={getImageUrl(payment.proofUrl)} target="_blank" rel="noopener noreferrer">
                      {payment.proofUrl.toLowerCase().endsWith('.pdf') ? (
                        <span><Paperclip size={12} /> {t('offlineSales.cpViewFile')}</span>
                      ) : (
                        <img src={getImageUrl(payment.proofUrl)} alt={t('offlineSales.cpProof')} className={styles.proofThumb} />
                      )}
                    </a>
                  ) : '—'}
                </dd>
              </div>
            </dl>
          </div>
        </>
      )}

      <h3 className={styles.sectionTitle}>{t('offlineSales.cpAllocations')}</h3>
      <DataTable
        data={allocations}
        columns={columns}
        loading={loading}
        rowClassName={(row) => (row.isVoid ? styles.voidRow : '')}
        emptyMessage={t('offlineSales.cpNoAllocations')}
      />
    </div>
  );
};

export default CustomerPaymentDetailPage;
